/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { useEditor, useNode } from '@craftjs/core';
import { useContextSelector } from 'use-context-selector';
import clsx from 'clsx';
import { DateRangeComponent } from '../../../daterangePicker/daterange.component';
import { convertDateToString, formatDateRange, toDateOrNull } from '../../../../helpers/date-helpers';
import { ValidationSettings } from '../../toolbox/settingsPanel/content/Validation.component';
import styles from '../controlsStyle.module.scss';
import {
  dateRangeInputDefaultPropsFactory,
  dateRangeApplicableValidationRules,
  IDateRangeInputProps,
  IDateRangeInput,
} from '../../../../features/forms/base/controls/inputs/DateRangeInput';
import {
  useRefForDataBlockNavigation,
  useFieldDataFromUiEditorContext,
  useFieldValidation,
  useSkipReasonText,
} from './base/hooks';
import { CheckboxSettings } from '../../toolbox/settingsPanel/settingsInputs/CheckboxSettings';
import { useTranslation } from 'react-i18next';
import { FormFillContext } from '../../uiEditor/provider/formFill.context';
import { LayoutSettings } from '../../toolbox/settingsPanel/content/LayoutSettings';
import { CommonFieldSettings } from '../../toolbox/settingsPanel/content/OtherSettings';
import { TitleSettings } from '../../toolbox/settingsPanel/content/TitleSettings';
import { ControlsGroup } from '../../toolbox/components/controlsGroup';
import { PanelContainer } from '../../toolbox/components/panelContainer';
import { IssueMark } from 'src/components/issue/issueTarget/issueMark.component';
import { OverviewField } from 'uikit/fields/overview/OverviewField';
import { OVERVIEW_FIELD_ISSUE_MARK_POSITION } from 'src/components/issue/issueTarget/issueMark-helper';

export const DateRangeInput: IDateRangeInput = (props) => {
  const { size, dataKey, hasSkipCheckBox, skipCheckBoxText } = props;
  const {
    connectors: { connect, drag },
    id,
  } = useNode();
  const { enabled: isConstructorMode } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { isSubmitting, formConfig, formResultVersion, patient, stepName } = useContextSelector(
    FormFillContext,
    (x) => x,
  );
  const { isOnlyView, setDataBlockFieldValue, onSkipReasonChange, fieldEditReason } =
    useFieldDataFromUiEditorContext(dataKey);

  const skippedFieldsData = useContextSelector(FormFillContext, (x) => x.skippedFieldsData);
  // The skip reason for fields from and until should be the same, because we have only one
  // skip reason component for both fields
  const skipReason = useMemo(() => skippedFieldsData?.[`${dataKey}_from`], [skippedFieldsData]);
  const skipReasonText = useSkipReasonText(skipReason, skipCheckBoxText);

  const inputValueStartDate = useContextSelector(
    FormFillContext,
    (v) => v.values?.[`${dataKey}_from`] as string | undefined,
  );
  const inputValueEndDate = useContextSelector(
    FormFillContext,
    (v) => v.values?.[`${dataKey}_until`] as string | undefined,
  );

  const { dataBlockValidation: startDataBlockValidation, triggerFieldValidation: triggerStartDataValidation } =
    useFieldValidation({
      isEditable: props.isEditable,
      isDisabled: props.isDisabled,
      rules: props.validation,
      fieldPostfix: '_from',
    });

  const { dataBlockValidation: endDataBlockValidation, triggerFieldValidation: triggerEndDataValidation } =
    useFieldValidation({
      isEditable: props.isEditable,
      isDisabled: props.isDisabled,
      rules: props.validation,
      fieldPostfix: '_until',
    });

  const blockRef = useRefForDataBlockNavigation();

  const editableComponent = (
    <div
      data-test-field-type={'date-range-input'}
      className={styles.container}
      style={{
        width: size,
      }}
      draggable={isConstructorMode}
      ref={(ref) => isConstructorMode && connect(drag(ref!))}
    >
      <div
        ref={blockRef}
        className={clsx({
          [styles.validationError]: !!startDataBlockValidation?.text || !!endDataBlockValidation?.text,
        })}
      >
        <DateRangeComponent
          isBold={props.isBold}
          hideLabel={props.hideLabel}
          label={props.label || dataKey}
          tooltip={props.tooltip}
          disabled={!props.isEditable || isSubmitting}
          startDate={
            !isConstructorMode
              ? inputValueStartDate
              : props.defaultValueFromIsNow
              ? convertDateToString(new Date())
              : undefined
          }
          endDate={
            !isConstructorMode
              ? inputValueEndDate
              : props.defaultValueUntilIsNow
              ? convertDateToString(new Date())
              : undefined
          }
          onStartChange={(e) => {
            const parsedTime: Date | null = e.target.value ? new Date(Date.parse(e.target.value)) : null;
            if (parsedTime?.toDateString() === 'Invalid Date') return;
            setDataBlockFieldValue?.(`${dataKey}_from`, e.target?.value || undefined);
          }}
          onEndChange={(e) => {
            const parsedTime: Date | null = e.target.value ? new Date(Date.parse(e.target.value)) : null;
            if (parsedTime?.toDateString() === 'Invalid Date') return;
            setDataBlockFieldValue?.(`${dataKey}_until`, e.target?.value || undefined);
          }}
          startDateErrorText={startDataBlockValidation?.text}
          endDateErrorText={endDataBlockValidation?.text}
          skipProps={{
            inputCanBeSkipped: hasSkipCheckBox,
            skipText: skipCheckBoxText,
            onSkipReasonChange: (x) => {
              onSkipReasonChange(`${dataKey}_from`)(x);
              onSkipReasonChange(`${dataKey}_until`)(x);
            },
            skipReason: skipReason,
          }}
          onStartDateBlur={triggerStartDataValidation}
          onEndDateBlur={triggerEndDataValidation}
        />
      </div>
    </div>
  );

  const viewComponent = (
    <IssueMark
      issueContext={{
        subject: 'Patient',
        topic: 'Records',
        topicAdditional: formConfig?.type,
        linkedPatientUniqId: patient?.uniqueId,
        fieldDescription: props.label ?? props.dataKey,
        resultId: formResultVersion?.formResultId,
        fieldId: id,
        stepName: formConfig?.isMultiInstance ? 'multiple' : stepName,
      }}
      ignoreFieldsForCount={['stepName']}
      position={OVERVIEW_FIELD_ISSUE_MARK_POSITION}
    >
      <OverviewField
        hideLabel={props.hideLabel}
        label={props.label ?? props.dataKey}
        skipped={skipReason !== undefined}
        skipReason={skipReasonText}
        value={formatDateRange(toDateOrNull(inputValueStartDate), toDateOrNull(inputValueEndDate))}
        editReason={fieldEditReason}
      />
    </IssueMark>
  );

  return <>{isOnlyView ? viewComponent : editableComponent}</>;
};

const DateRangeInputGeneralSettings = () => {
  const { t } = useTranslation('dev');
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as IDateRangeInputProps,
  }));

  return (
    <PanelContainer header={t('StudySettingsPage.FormBuilder.settings.generalSettings')} expanded={false}>
      <ControlsGroup flexible>
        <CheckboxSettings
          label={t('StudySettingsPage.FormBuilder.settings.defaultValueFromAsNow')}
          isChecked={!!props.defaultValueFromIsNow}
          setValue={(newValue) =>
            setProp((setProps: IDateRangeInputProps) => (setProps.defaultValueFromIsNow = newValue))
          }
        />

        <CheckboxSettings
          label={t('StudySettingsPage.FormBuilder.settings.defaultValueToAsNow')}
          isChecked={!!props.defaultValueUntilIsNow}
          setValue={(newValue) =>
            setProp((setProps: IDateRangeInputProps) => (setProps.defaultValueUntilIsNow = newValue))
          }
        />
      </ControlsGroup>
    </PanelContainer>
  );
};

const DateRangeInputSettings = () => {
  return (
    <>
      <TitleSettings />
      <DateRangeInputGeneralSettings />
      <LayoutSettings />
      <ValidationSettings applicableRules={dateRangeApplicableValidationRules} />
      <CommonFieldSettings />
    </>
  );
};

DateRangeInput.craft = {
  props: dateRangeInputDefaultPropsFactory(),
  related: {
    settings: DateRangeInputSettings,
  },
};
